import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDialogListener, useNotify } from '@/composables';
import { transformCreateDataToApiRequestData } from '../components/EventTriggeredCreateTaskDrawer.helpers';
import yardTasksService from '@satellite/services/yard-tasks-service';

import {
  OpenCreateYardCheckTaskDialogEvent,
  OpenCreateYardMoveTaskDialogEvent
} from '@/composables/useEventBasedDialog';

import type { CheckTaskCreateData, MoveTaskCreateData } from '@nova/core';

type TaskType =
  | OpenCreateYardCheckTaskDialogEvent['type']
  | OpenCreateYardMoveTaskDialogEvent['type'];
type TaskCreateData = CheckTaskCreateData | MoveTaskCreateData;

type CreateYardTaskDialogEventName =
  | 'open-create-yard-task-drawer'
  | 'open-create-yard-move-task-drawer';

type UseCreateYardTaskDrawerOptions<K extends CreateYardTaskDialogEventName> = {
  dialogEventName: K;
  defaultTaskType?: TaskType;
};

export function useCreateYardTaskDrawer<K extends CreateYardTaskDialogEventName>(
  options: UseCreateYardTaskDrawerOptions<K>
) {
  const { dialogEventName, defaultTaskType = 'move' } = options;
  const { t } = useI18n();
  const notify = useNotify();

  const {
    isDialogOpen,
    dialogProps,
    closeDialog,
    getDialogProp,
    subscribeToDialogEvent,
    unsubscribeFromDialogEvent
  } = useDialogListener<K>(dialogEventName);

  onMounted(() => {
    subscribeToDialogEvent();
  });

  onUnmounted(() => {
    unsubscribeFromDialogEvent();
  });

  const tab = ref<TaskType>(defaultTaskType);
  const isSubmitDisabled = ref(true);
  const currentTaskData = ref<TaskCreateData | null>(null);

  const initializeCheckTask = (): CheckTaskCreateData => {
    // Type assertion: we know this is a check task, so we can access check-specific properties
    const checkProps = dialogProps.value as OpenCreateYardCheckTaskDialogEvent;
    return {
      type: 'check',
      subject: checkProps?.subject ?? '',
      spot: checkProps?.spot ?? undefined,
      area: checkProps?.area ?? undefined,
      checklist: checkProps?.checklist ?? [],
      category: checkProps?.category ?? undefined
    };
  };

  const initializeMoveTask = (): MoveTaskCreateData => {
    // Type assertion: we know this is a move task, so we can access move-specific properties
    const moveProps = dialogProps.value as OpenCreateYardMoveTaskDialogEvent;
    return {
      type: 'move',
      asset: moveProps?.asset ?? undefined,
      originSpot: moveProps?.originSpot ?? undefined,
      destinationSpot: moveProps?.destinationSpot ?? undefined
    };
  };

  const initializeTask = (taskType: TaskType): TaskCreateData => {
    const baseTask = taskType === 'check' ? initializeCheckTask() : initializeMoveTask();
    return {
      ...baseTask,
      assignee: getDialogProp('assignee') ?? undefined,
      dueByDate: getDialogProp('dueByDate') ?? undefined,
      dueByTime: getDialogProp('dueByTime') ?? undefined,
      isUrgent: getDialogProp('isUrgent') ?? undefined
    } as TaskCreateData;
  };

  watch(
    isDialogOpen,
    newIsOpen => {
      if (!newIsOpen) {
        tab.value = defaultTaskType;
        currentTaskData.value = initializeTask(defaultTaskType);
      } else {
        const taskType = (dialogProps.value?.type ?? defaultTaskType) as TaskType;
        tab.value = taskType;
        currentTaskData.value = initializeTask(taskType);
      }
    },
    { immediate: true }
  );

  watch(tab, () => {
    isSubmitDisabled.value = true;
    currentTaskData.value = initializeTask(tab.value);
  });

  const handleValidate = (isValid: boolean) => {
    isSubmitDisabled.value = !isValid;
  };

  const createTask = async () => {
    if (!dialogProps.value) {
      console.error('Failed to create task: No dialog props found');
      return;
    }

    const payload = transformCreateDataToApiRequestData(
      currentTaskData.value,
      dialogProps.value?.warehouseId,
      dialogProps.value?.warehouseTimezone
    );

    if (!payload) {
      console.error('Failed to build task payload');
      return;
    }

    try {
      const task = await yardTasksService.createYardTask(payload);

      notify(t(`yard.messages.${currentTaskData.value.type}TaskCreated`), 'success');

      dialogProps.value.onTaskCreated?.(task);
      closeDialog();
    } catch (error) {
      console.error('Failed to create yard task:', error);
      notify(t('yard.messages.failedToCreateYardTask'), 'error');
    }
  };

  return {
    tab,
    isDialogOpen,
    isSubmitDisabled,
    currentTaskData,
    dialogProps,

    getDialogProp,
    closeDialog,
    createTask,
    handleValidate,

    initializeTask,
    initializeCheckTask,
    initializeMoveTask,
    t
  };
}
